import {
  EventEmitter,
  EventEmitterWithIndex,
  GlobalAIHook,
  useActivityTracker,
  useIsAuthorisedHook,
  useProduct,
  useUserMSPCheckHook,
  withErrorBoundary,
  withPlanFeatureGating
} from '@nile/components';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import { SVGIcon } from '@superops/super-icons';
import { Button, TextStyle } from '@superops/ui-design-system';
import Tippy from '@tippyjs/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import 'tippy.js/animations/scale-extreme.css';

const AIAgentIcon = ({
  icon,
  defaultIconUrl,
  overrideIcon,
  name,
  size = 'sm'
}) => {
  if ((icon && overrideIcon) || (!defaultIconUrl && icon)) {
    return (
      <div className="flex ignoreOverride">
        <SVGIcon name={icon} size={size} />
      </div>
    );
  }

  return (
    <div className={`flex ai-agent-activator-icon-wrapper ${size}`}>
      <img
        src={defaultIconUrl}
        alt={name}
        className="ai-agent-activator-icon w100"
      />
    </div>
  );
};

const ToolTipWrapper = (props) => {
  const { title, children, appendTo } = props;
  const tippyRef = React.useRef(null);
  const tooltipRef = React.useRef(null);

  if (title) {
    return (
      <div className="position-relative d-in " ref={tooltipRef}>
        <Tippy
          content={
            <div className="ai-activator-tippy text-white">
              <TextStyle>{title}</TextStyle>
            </div>
          }
          zIndex={3947483000}
          appendTo={appendTo || tooltipRef.current || document.body}
          animation="scale-extreme"
          allowHTML
          ref={(node) => {
            tippyRef.current = node;
          }}
          className="ai-activator-tippy-wrapper"
        >
          {children}
        </Tippy>
      </div>
    );
  } else {
    return <>{children}</>;
  }
};

const AIActivator = ({
  name, // To getAIprops based on name. We'll check the permission based on featureName but AI props can be through name or featureName
  featureName, // To get AIProps. If name is not passed we'll check by getAIprops
  icon,
  overrideIcon = true, // By default we'll accept the icon passed by overriding it. If you need the displayIcon url. pass override false
  customProps = {},
  disabledTooltip,
  progressTooltip,
  enabledTooltip,
  uiType,
  roleToCheck,
  roleCheckType,
  label,
  buttonText,
  buttonVariant = 'plain',
  buttonSize = 'medium',
  mixpanelEventOnClick = null,
  mixpanelEventOnCopy = null,
  getMixpanelEventOnCopy = null,
  onClick,
  appendTo,
  productToCheck,
  flagToCheck,
  fullWidth = false,
  hideIcon = false,
  customClassName = '',
  type,
  listenDefaultAgent
}) => {
  const flags = useFlags();
  const isMSP = useUserMSPCheckHook();

  const isFlagEnabled = flagToCheck ? flags?.[flagToCheck] : true;

  const aiFeatureHook = AIFeaturesHook(); // To check if feature is enabled with the backend data
  const globalAIHook = GlobalAIHook(); // To get AI props based on Config
  const { getAIProps } = globalAIHook;
  const { getIsAIFeatureEnabled, getAIFeatureData } = aiFeatureHook;

  const getButtonVariant = () => {
    switch (buttonVariant) {
      case 'primary':
        return {
          primary: true
        };
      case 'secondary':
        return {
          secondary: true
        };
      case 'aiPrimary':
        return {
          aiPrimary: true
        };
      case 'aiPrimaryLight':
        return {
          aiPrimaryLight: true
        };
      case 'aiPrimaryFill':
        return {
          aiPrimaryFill: true
        };
      case 'default':
        return {};
      default:
        return {
          plain: true
        };
    }
  };

  const buttonVariantData = getButtonVariant();

  const [isOpen, setIsOpen] = useState(false);

  const getIsFeatureAvailable = () => {
    const roleCheck = roleToCheck
      ? typeof roleToCheck === 'string'
        ? [roleToCheck]
        : roleToCheck
      : null;
    const checkType = roleCheckType || 'AND';
    if (roleCheck) {
      let isAuthorised = true;

      const authorisedArray = roleCheck.map((role) =>
        useIsAuthorisedHook(role)
      );

      if (checkType === 'AND') {
        // Check if all elements are true
        isAuthorised = authorisedArray.every(Boolean);
      } else if (checkType === 'OR') {
        // Check if any element is true
        isAuthorised = authorisedArray.some(Boolean);
      }

      return isAuthorised;
    }

    return true;
  };

  const aiFeatureData = getAIFeatureData(featureName);

  const defaultIconUrl = aiFeatureData?.displayIconUrl;

  const getTooltipTitle = () => {
    const aiState = aiFeatureData?.state;
    const isDisabled =
      aiState === 'INACTIVE' ||
      aiState === 'DISABLED_IN_PROGRESS' ||
      !aiFeatureData?.enabledForLoggedInUser;
    const inProgress =
      aiState === 'IN_PROGRESS' || aiState === 'ENABLED_IN_PROGRESS';
    if (isDisabled) {
      return disabledTooltip || null;
    }
    if (inProgress) {
      return progressTooltip || null;
    }
    return enabledTooltip || null;
  };

  let isAvailableInProduct = true;

  if (productToCheck) {
    isAvailableInProduct = useProduct(productToCheck);
  }

  const isAvailable =
    getIsFeatureAvailable(featureName) && isAvailableInProduct && isFlagEnabled;
  // To check if feature is available based on role

  const isEnabled = isAvailable ? getIsAIFeatureEnabled(featureName) : false;
  // To check if feature is enabled or disabled in settings

  const disabled = !isEnabled;

  const title = getTooltipTitle();

  const emitEvent = () => {
    if (isEnabled) {
      let aiProps = getAIProps({
        name: name || null,
        feature: {
          featureName: featureName
        },
        customProps: customProps
      });

      aiProps = {
        ...aiProps,
        showRatingOnCopy: aiProps?.showRatingOnCopy || type === 'REPHRASE',
        mixpanelEventOnCopy:
          mixpanelEventOnCopy || aiProps?.mixpanelEventOnCopy,
        getMixpanelEventOnCopy:
          getMixpanelEventOnCopy || aiProps?.getMixpanelEventOnCopy
      };
      const mixpanelOnClick =
        mixpanelEventOnClick || aiProps?.mixpanelEventOnClick;

      if (aiProps?.onClick) {
        aiProps?.onClick();
      }
      setIsOpen(true);
      EventEmitter.dispatch('openAISummary', aiProps);
      if (mixpanelOnClick) {
        useActivityTracker(mixpanelOnClick);
      }
      if (onClick) {
        onClick({
          data: aiProps
        });
      }
    }
  };

  const closeAISummary = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    let listenDefaultAgentIndex;
    if (listenDefaultAgent) {
      listenDefaultAgentIndex = EventEmitterWithIndex.subscribe(
        'listenDefaultAgent',
        emitEvent
      );
    }
    EventEmitter.subscribe('AISummaryClosed', closeAISummary);
    return function cleanup() {
      EventEmitter.unsubscribe('AISummaryClosed');
      if (listenDefaultAgentIndex) {
        EventEmitterWithIndex.unsubscribe(
          'listenDefaultAgent',
          listenDefaultAgentIndex
        );
      }
    };
  }, []);

  if (isMSP && isAvailable) {
    return (
      <>
        {uiType === 'Option' ? (
          <ToolTipWrapper title={title} appendTo={appendTo}>
            <div className={`${fullWidth ? 'fw' : ''} `}>
              <Button
                icon={
                  <AIAgentIcon
                    icon={icon}
                    defaultIconUrl={defaultIconUrl}
                    overrideIcon={overrideIcon}
                    name={name}
                  />
                }
                aiPrimary
                size="slim"
                onClick={emitEvent}
                disabled={disabled}
              >
                <div className="fsize12">
                  <FormattedMessage id={label} />
                </div>
              </Button>
            </div>
          </ToolTipWrapper>
        ) : (
          <>
            <ToolTipWrapper title={title} appendTo={appendTo}>
              {buttonText ? (
                <div className={`${fullWidth ? 'fw' : ''} `}>
                  <Button
                    onClick={emitEvent}
                    icon={
                      !hideIcon && (
                        <AIAgentIcon
                          icon={icon}
                          defaultIconUrl={defaultIconUrl}
                          overrideIcon={overrideIcon}
                          name={name}
                        />
                      )
                    }
                    disabled={!isEnabled}
                    size={buttonSize}
                    {...buttonVariantData}
                    fullWidth={fullWidth}
                  >
                    {buttonText}
                  </Button>
                </div>
              ) : (
                <div
                  className={`${customClassName} ${
                    isEnabled ? 'pointer' : 'disabled opacity5'
                  } flex w-fit actr`}
                  onClick={emitEvent}
                >
                  {!hideIcon && (
                    <AIAgentIcon
                      icon={icon}
                      defaultIconUrl={defaultIconUrl}
                      overrideIcon={overrideIcon}
                      name={name}
                    />
                  )}

                  {label && (
                    <div className="flex">
                      <TextStyle>{label}</TextStyle>
                    </div>
                  )}
                </div>
              )}
            </ToolTipWrapper>
          </>
        )}
      </>
    );
  }

  return <></>;
};

// export default withErrorBoundary(AIActivator);

function AIActivatorWrapper(props) {
  const isMSP = useUserMSPCheckHook();

  if (!isMSP) return null;

  return <AIActivator {...props} />;
}

export default withPlanFeatureGating(withErrorBoundary(AIActivatorWrapper), {
  feature: 'AI_FEATURES',
  mode: 'hide',
  id: 'AIActivator'
});
